import classNames from 'classnames';
import camelCase from 'lodash-es/camelCase';
// import { FormattedMessage } from 'react-intl';
import React, { ReactNode, CSSProperties } from 'react';
import { Link } from 'wouter';

import useDocumentCard from '../../hooks/useDocumentCard';
import normalizeCardTheme from '../../lib/normalizeCardTheme';

import UrbaniaHeader from '../headers/UrbaniaHeader';
import PlayIcon from '../icons/PlayIcon';
import Author from '../partials/Author';
import Category from '../partials/Category';
import Image from '../partials/Image';
import Sponsor from '../partials/Sponsor';
import Surtitle from '../partials/Surtitle';
import Time from '../partials/Time';
import CardDescription from '../typography/CardDescription';
import DocumentMetadata from '../typography/DocumentMetadata';
import DocumentTitle from '../typography/DocumentTitle';

import styles from '../../styles/cards/document-featured-card.module.css';

export interface DocumentFeaturedCardProps extends Partial<DocumentSnippet> {
    linkQuery?: Record<string, unknown> | null;
    authorPrefix?: string | null;
    theme?: CardTheme | LegacyCardTheme | null;
    placeholder?: boolean;
    size?: CardSize | null;
    ratio?: CardRatio | null;
    responsive?: boolean;
    imageLoading?: Loading;
    imageFetchPriority?: 'high' | 'low' | 'auto' | null;
    label?: ReactNode;
    imageSize?: string | null;
    pictureStyle?: CSSProperties | null;
    withCoverImage?: boolean;
    withBorderRadius?: boolean;
    withoutText?: boolean;
    withCategory?: boolean;
    withoutSurtitle?: boolean;
    withoutAuthor?: boolean;
    withoutAuthorBox?: boolean;
    withoutAuthorImage?: boolean;
    withoutDate?: boolean;
    withoutDescription?: boolean;
    withoutSponsor?: boolean;
    withBreakpoints?: boolean;
    withUrbaniaLogo?: boolean;
    withMicromagSummary?: boolean;
    withPlayButton?: boolean;
    withoutLink?: boolean;
    withImageLink?: boolean;
    withoutLabel?: boolean;
    innerClassName?: string | null;
    titleClassName?: string | null;
    labelClassName?: string | null;
    imageContainerClassName?: string | null;
    imageClassName?: string | null;
    className?: string | null;
}

function DocumentFeaturedCard({
    id = null,
    slug = null,
    linkQuery = null,
    type = null,
    image = null,
    coverImage = null,
    categories = null,
    credits = null,
    authors = null,
    sponsors = null,
    title = null,
    surtitle = null,
    date = null,
    topic = null,
    format = null,
    topics = null,
    formats = null,
    collection = null,
    season = null,
    episode = null,
    micromagNumber = null,
    description = null,
    authorPrefix = null,
    theme: providedTheme = null,
    size = null,
    ratio = null,
    responsive = false,
    placeholder = false,
    imageLoading = 'lazy',
    imageFetchPriority = null,
    label = null,
    imageSize = null,
    pictureStyle = null,
    withCoverImage = false,
    withBorderRadius = false,
    withoutText = false,
    withCategory = false,
    withoutSurtitle = false,
    withoutDate = false,
    withoutDescription = false,
    withoutAuthor = false,
    withoutAuthorBox = false,
    withoutAuthorImage = false,
    withoutSponsor = false,
    withBreakpoints = false,
    withUrbaniaLogo = false,
    withMicromagSummary = false,
    withPlayButton = false,
    withoutLink = false,
    withImageLink = false,
    withoutLabel = false,
    innerClassName = null,
    labelClassName = null,
    titleClassName = null,
    imageContainerClassName = null,
    imageClassName = null,
    className = null,
}: DocumentFeaturedCardProps) {
    const theme = normalizeCardTheme(providedTheme);
    const {
        href,
        category,
        author,
        sponsor,
        surtitle: finalSurtitle,
    } = useDocumentCard(
        {
            type,
            slug,
            surtitle,
            categories,
            credits,
            authors,
            sponsors,
            topic,
            format,
            topics,
            formats,
            collection,
            season,
            episode,
            micromagNumber,
            date,
        },
        {
            linkQuery,
            placeholder,
            surtitle: {
                withCategory: !withCategory,
                withoutMicromagDate: true,
            },
        },
    );

    const isOver = theme !== null && theme.indexOf('over') === 0;
    const isBox = theme !== null && theme.indexOf('box') === 0;
    const isOutside = !isOver;

    // const finalRatio = ratio !== null ? getRatio(ratio) : null;

    const CardComponent: any = slug !== null && !withoutLink ? Link : 'div';

    const imageElement = (
        <Image
            className={classNames([styles.image, imageClassName])}
            pictureClassName={styles.picture}
            media={withCoverImage ? coverImage || image : image}
            size={imageSize || 'responsive'}
            loading={imageLoading}
            fetchPriority={imageFetchPriority}
            withPlaceholder={placeholder}
            pictureStyle={pictureStyle || undefined}
        />
    );

    const meta = [
        (author !== null || placeholder) && !withoutAuthor ? (
            <Author
                key="meta-author"
                {...author}
                prefix={authorPrefix}
                placeholder={placeholder}
                withBox={!withoutAuthorBox}
                withoutImage={withoutAuthorImage}
                className={classNames([
                    styles.author,
                    {
                        [styles.withBox]: !withoutAuthorBox,
                    },
                ])}
            />
        ) : null,
        date !== null && !withoutDate ? (
            <DocumentMetadata className={styles.date} key="meta-date">
                <Time date={date} />
            </DocumentMetadata>
        ) : null,
    ].filter((it) => it !== null);

    return (
        <CardComponent
            href={!withoutLink ? href : '#'}
            data-card-theme={theme !== null ? theme : undefined}
            className={classNames([
                styles.container,
                {
                    [styles.responsive]: responsive,
                    [styles.isOver]: isOver,
                    [styles.isOutside]: isOutside,
                    [styles.isBox]: isBox,
                    [styles.withRatio]: ratio !== null,
                    [styles.withoutText]: withoutText && !withMicromagSummary,
                    [styles.withBorderRadius]: withBorderRadius,
                    [styles.withBreakpoints]: withBreakpoints,
                },
                styles[camelCase(ratio || 'none')],
                styles[camelCase(size as string)],
                className,
            ])}
        >
            <span className={classNames([styles.inner, innerClassName])}>
                {label !== null ? (
                    <div className={classNames([styles.label, labelClassName])}>{label}</div>
                ) : !withoutLabel ? (
                    <span className={classNames([styles.label, labelClassName])}>
                        <span className={styles.before}>
                            {(category !== null || placeholder) && withCategory ? (
                                <Category
                                    {...category}
                                    placeholder={placeholder}
                                    className={styles.category}
                                />
                            ) : null}
                            {finalSurtitle !== null && !withoutSurtitle ? (
                                <Surtitle surtitle={finalSurtitle} className={styles.surtitle} />
                            ) : null}
                        </span>
                        <DocumentTitle
                            className={classNames([styles.title, titleClassName])}
                            placeholder={placeholder}
                            placeholderLines={3}
                        >
                            {title}
                        </DocumentTitle>
                        <span className={styles.after}>
                            {description !== null && !withoutDescription ? (
                                <CardDescription
                                    className={styles.description}
                                    html={description}
                                />
                            ) : null}
                            {meta.length > 0 ? <div className={styles.meta}>{meta}</div> : null}
                            {sponsor !== null && !withoutSponsor ? (
                                <Sponsor
                                    {...sponsor}
                                    iconOnly
                                    className={styles.sponsor}
                                    labelClassName={styles.sponsorLabel}
                                />
                            ) : null}
                        </span>
                    </span>
                ) : null}
                <span className={classNames([styles.imageContainer, imageContainerClassName])}>
                    {withImageLink ? <Link href={href || '#'}>{imageElement}</Link> : imageElement}
                    {withPlayButton ? (
                        <span className={styles.playIcon}>
                            <PlayIcon className={styles.icon} />
                        </span>
                    ) : null}
                    {withUrbaniaLogo ? <UrbaniaHeader className={styles.urbaniaLogo} /> : null}
                </span>
            </span>
        </CardComponent>
    );
}

export default React.memo(DocumentFeaturedCard);
